import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";

import { useGetPermissions } from "Flocknote/API/queries";
import { useInvalidateNetworkMetadata } from "Flocknote/API/queryHooks";

import {
  createPrayerRequest,
  deletePrayerRequest,
  deletePrayerRequestReaction,
  editNetworkPreferences,
  getPrayerRequestMetadata,
  getPrayerRequestStats,
  getPrayerRequests,
  markPostAsSeen,
  reactToPrayerRequest,
  updateNetworkSetting,
} from "./fetch";
import { prayerRequestsKeys } from "./keys";

export const useGetPrayerRequests = ({ getNextPageParam, setErrorMessage } = {}) => {
  return useInfiniteQuery({
    queryKey: prayerRequestsKeys.prayerRequests(),
    queryFn: ({ pageParam: nextPageCursor }) =>
      getPrayerRequests({
        nextPageCursor,
      }),
    onError: (error) => {
      setErrorMessage?.(error.message);
    },
    getNextPageParam,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useGetPrayerRequestMetadata = () => {
  return useQuery({
    queryKey: prayerRequestsKeys.metadata(),
    queryFn: getPrayerRequestMetadata,
    refetchOnWindowFocus: false,
  });
};

// Fires before permissions load so authorized users get data ASAP. If the
// user turns out to lack prayer request access, the single pre-permissions
// request may fail — retry is 0 until permissions arrive to avoid wasted
// retries. Once permissions resolve, enabled flips to false for unauthorized users.
export const useGetPrayerRequestStats = () => {
  const { data: permissions } = useGetPermissions();

  return useQuery({
    queryKey: prayerRequestsKeys.stats(),
    queryFn: getPrayerRequestStats,
    refetchOnWindowFocus: false,
    enabled: !permissions || Boolean(permissions.canSeePrayerPortalNav),
    retry: permissions ? undefined : 0,
  });
};

export const useMarkAsSeen = () => {
  return useMutation({
    mutationFn: ({ seenPrayerRequests }) => {
      return markPostAsSeen(seenPrayerRequests);
    },
  });
};

export const useReactToPrayerRequest = (
  { onMutate, onError, onSuccess } = {
    onMutate: () => { },
    onError: () => { },
    onSuccess: () => { },
  }
) => {
  return useMutation({
    mutationFn: (reaction) => {
      return reactToPrayerRequest(reaction);
    },
    onMutate,
    onSuccess,
    onError,
  });
};

export const useDeletePrayerRequest = (
  { onMutate, onSuccess, onError } = {
    onMutate: () => { },
    onSuccess: () => { },
    onError: () => { },
  }
) => {
  return useMutation({
    mutationFn: (prayerRequest) => {
      return deletePrayerRequest(prayerRequest);
    },
    onMutate,
    onSuccess,
    onError,
  });
};

export const useDeletePrayerRequestReaction = (
  { onMutate, onSuccess, onError } = {
    onMutate: () => { },
    onSuccess: () => { },
    onError: () => { },
  }
) => {
  return useMutation({
    mutationFn: (reaction) => {
      return deletePrayerRequestReaction(reaction);
    },
    onMutate,
    onSuccess,
    onError,
  });
};

export const useCreatePrayerRequest = ({ onSuccess, onError } = {}) => {
  return useMutation({
    mutationFn: ({ message }) => {
      return createPrayerRequest({ message });
    },
    onSuccess,
    onError,
  });
};

export const useUpdateNetworkSetting = () => {
  const invalidateNetworkMetadata = useInvalidateNetworkMetadata();
  return useMutation({
    mutationFn: ({ settingName, value }) => {
      return updateNetworkSetting({
        settingName,
        value,
      });
    },
    onSuccess: () => {
      invalidateNetworkMetadata();
    },
  });
};

export const useEditNetworkPreferences = ({
  onSuccess,
  onError,
  onMutate,
} = {}) => {
  return useMutation({
    mutationFn: ({ userID, preferenceKey, preferenceValue }) => {
      return editNetworkPreferences({
        userID,
        preferenceKey,
        preferenceValue,
      });
    },
    onMutate,
    onSuccess,
    onError,
  });
};
